import { useState } from 'react';
import { api, ApiError } from '../api/client';
import { useProgress } from '../state/ProgressContext';
import { TrainingStepper } from '../components/TrainingStepper';
import { HvacOnboardingSteps } from '../components/HvacOnboardingSteps';
import { HVAC_MODULES, HVAC_PORTAL_STEP_BODIES, HVAC_PORTAL_STEP_TITLES, type WorkMode } from '../content/hvac';

/**
 * HVAC Speed-to-Lead training: the product, selling, the demo and the lead routine - each saved on
 * the server as it is finished (so leaving and coming back resumes where they stopped) - and finally the first real lead in the workspace, which the Portal
 * confirms step by step. The last Portal step activates the rep.
 */
export function HvacTraining({ workMode }: { workMode: WorkMode }) {
  const { progress, refresh } = useProgress();
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<string | null>(null);
  if (!progress) return null;

  const done = new Set(progress.training.completedStepKeys);
  const currentModule = HVAC_MODULES.find((module) => !done.has(module.key)) ?? null;
  const portalSteps = progress.training.steps.filter((key) => !key.startsWith('HVAC_'));
  const approved = Boolean(progress.training.approvedAt);

  async function completeModule(key: string) {
    setSaving(key); setError(null);
    try {
      await api.post(`/api/progress/training/modules/${key}/complete`);
      await refresh();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (failure) { setError(failure instanceof ApiError ? failure.message : 'Could not save. Please try again.'); }
    finally { setSaving(null); }
  }

  return <div className="first-lead-training">
    <h1 className="step-title">Training</h1>
    <p className="step-subtitle">Read each section, then mark it done. Your progress saves as you go.</p>
    <HvacOnboardingSteps progress={progress} />
    {approved && <div className="alert success">
      <strong>You are active.</strong> Your onboarding is complete - your leads and workspace are ready.
      <p><a className="btn-primary" href="https://portal.krovance.com">Go to your workspace</a></p>
    </div>}
    {progress.training.changesFeedback && !approved && <div className="alert warning"><strong>Changes requested:</strong> {progress.training.changesFeedback}</div>}

    {HVAC_MODULES.map((module, index) => {
      const complete = done.has(module.key) || approved;
      const current = module.key === currentModule?.key && !approved;
      const open = current || reviewing === module.key;
      return <section key={module.key} className="card hvac-module" aria-current={current ? 'step' : undefined}>
        <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>{complete ? '✓ ' : ''}Part {index + 1}: {module.title}</h2>
        {!complete && !current && <p className="training-stepper-locked-note">Finish the section above first.</p>}
        {complete && !open && <button type="button" className="link-button" onClick={() => setReviewing(module.key)}>Read again</button>}
        {open && <>
          {module.body(workMode)}
          {current && <button type="button" className="btn-primary" disabled={saving !== null} onClick={() => void completeModule(module.key)}>{saving === module.key ? 'Saving…' : module.button}</button>}
          {complete && <button type="button" className="btn-secondary" onClick={() => setReviewing(null)}>Close</button>}
        </>}
      </section>;
    })}
    {error && <div className="alert error" role="alert">{error}</div>}

    <section className="card">
      <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>Part {HVAC_MODULES.length + 1}: Your first lead in your workspace</h2>
      {currentModule && !approved
        ? <p className="training-stepper-locked-note">Finish the sections above first. You can already sign in to your workspace, but these steps are counted in order.</p>
        : <p>Do your first real lead now. Each step below checks itself off as your workspace confirms it - there is nothing to submit here. When the last one is done, you are active.</p>}
      <TrainingStepper
        steps={portalSteps.map((key) => ({ key, title: HVAC_PORTAL_STEP_TITLES[key] ?? key, content: HVAC_PORTAL_STEP_BODIES[key] ?? null }))}
        completedKeys={progress.training.completedStepKeys}
        currentKey={progress.training.currentStepKey}
      />
    </section>
  </div>;
}
